import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { chatbotIsActiveOnConnectedPageSelector } from '@mm/state/selectors/viewSelectors';
import React, { useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { compose } from 'recompose';
import 'react-toggle/style.css';
import CollapsibleContentContainer from '../../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import { RouterComponentProps } from '../../../../../../common/helpers/routes/interfaces';
import { useGreenCueMarkers } from '../../../../../../common/hooks/use-green-cue-markers';
import { useFreeTrialUpgradeModal } from '../../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../../../common/widgets/common/PathGraph/PathGraph';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { useEditPagePrimeState } from '../../../../../common/hooks/use-edit-page-prime-state';
import { useShowInactiveBannerFlag } from '../../../../../common/hooks/use-show-inactive-bot-banner-flag';
import { InactiveBotAlert } from '../../../../../components/InactiveBotAlert/InactiveBotAlert';
import { DialogueSelectorWithReload } from '../../../common/DialogueSelectorWithReload/DialogueSelectorWithReload';
import { EditStartBotHeader } from '../../../common/StartBotPromoterPanel/EditStartBotHeader/EditStartBotHeader';
import { StartBotsDialogueEditor } from '../../../common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import FrequencyCapControl from '../../components/CommentGuardForm/FrequencyCapControl';
import { EditFBPost } from '../../components/EditFBPost/EditFBPost';
import { mapFormStateToProps } from '../../mappers/stateToPropsMapper';
import './EditCommentGuardPanel.scss';
import { InactiveChannelAlertSmart } from '../../../../campaigns/panels/DripCampaignDetailsPanel/InactiveChannelAlert';
import { DialoguePreview } from '../../../../../../modules/DailoguePreview/DialoguePreview';

type OuterProps = {};

type InnerProps = ReturnType<typeof mapFormStateToProps> & RouterComponentProps;

type Props = OuterProps & InnerProps;

const EditCommentGuardPanel = (props: Props) => {
  const { chatbotId, bot } = props;
  const { widgets, page, promoter } = useEditPagePrimeState('fb_comment_guard');

  let orderNumber = 0;

  const nextOrderNumber = () => {
    orderNumber += 1;
    return orderNumber.toString();
  };

  const {
    isFreeTrialPromotionWithoutSubscription,
    isFreeTrialPromotionActive,
  } = useFreeTrialUpgradeModal();

  const showActivationCues = useSelector(
    combinedFeatureFlagsSelector,
  ).show_cues;
  const { openActivateInactiveBotModal } = useModalActions();
  const { onClickActivate } = useShowInactiveBannerFlag();
  const chatbotIsActiveOnConnectedPage = useSelector(
    chatbotIsActiveOnConnectedPageSelector,
  );
  const { isCommentGuardActivated } = useGreenCueMarkers();
  const promoterCRUDActions = usePromoterCRUDActions();
  const modalActions = useModalActions();
  const updatePromoter = () => {
    if (promoter) {
      return promoterCRUDActions.update(chatbotId, {
        ...promoter,
        active: !promoter.active,
      });
    }
  };
  const customActivateAction = async () => {
    if (promoter) {
      if (!chatbotIsActiveOnConnectedPage && !promoter.active) {
        openActivateInactiveBotModal({
          onBotActivateConfirm: () =>
            setTimeout(() => onClickActivate(updatePromoter), 500),
          useCustomOnBotActivateConfirm: true,
        });
      } else {
        await updatePromoter();
        if (!isCommentGuardActivated) {
          setTimeout(
            () =>
              showActivationCues &&
              isFreeTrialPromotionWithoutSubscription &&
              modalActions.openFeatureActivationCongratsModal({
                feature: 'comment_guard',
              }),
            1500,
          );
        }
      }
    }
  };

  const pathGraph = useNewGraphInstance(promoter?.pageId);

  const dialogueSelectorDropdown = useMemo(() => {
    const promoterDialogueUpdate = async (dialogueId: number) => {
      if (promoter && promoter.id) {
        const updatedPromoter = {
          ...promoter,
          pageId: dialogueId,
        };
        const savedPromoter = await promoterCRUDActions.update(
          chatbotId,
          updatedPromoter,
        );

        const newDialogueId = savedPromoter?.pageId;

        if (newDialogueId) {
          const newUrl = BotBuilderRoutes.commentGuardEdit.getLink({
            chatbotId,
            dialogueId: newDialogueId,
            promoterId: promoter.id,
          });
          return {
            newDialogueId,
            newUrl,
          };
        }
      }
    };

    return promoter?.pageId ? (
      <DialogueSelectorWithReload
        dialogueId={promoter.pageId}
        entityUpdateFunction={promoterDialogueUpdate}
        forFacebook
      />
    ) : null;
  }, [promoter, chatbotId]);

  if (promoter && page) {
    return (
      <div className="edit-comment-guard-wrapper">
        <InactiveChannelAlertSmart facebook type="Comment Guard" />
        <EditStartBotHeader
          promoter={promoter}
          page={page}
          botIsTemplate={!!bot.template}
          customActivateAction={customActivateAction}
          showGreenMarker={
            !isCommentGuardActivated && isFreeTrialPromotionActive
          }
        />
        <div className="edit-comment-guard-panel__main-content-container">
          <div className="edit-comment-guard-panel__editor">
            <InactiveBotAlert />

            <CollapsibleContentContainer
              orderNumber={nextOrderNumber()}
              containerName="Choose whether to apply this comment guard to a single Facebook post or to all"
            >
              <EditFBPost guard={promoter} botId={chatbotId} />
            </CollapsibleContentContainer>

            <CollapsibleContentContainer
              orderNumber={nextOrderNumber()}
              containerName="Choose or create a Dialogue for users who engage with your Comment Guard"
            >
              {promoter && (
                <PathGraphContext.Provider value={pathGraph}>
                  {dialogueSelectorDropdown}
                  <StartBotsDialogueEditor
                    chatbotId={chatbotId}
                    widgets={widgets}
                    dialogueId={promoter.pageId}
                    restrictToChannels={['facebook']}
                    platforms={['facebook']}
                    showAttributesStartWarning
                    firstWidgetNotAllowedWarning={{
                      showWarning: true,
                      message: `This widget type is NOT supported as the first message in your Facebook Comment Guard. 
                      Please use a Quick Question widget instead.`,
                      startbotType: 'facebook_cg',
                    }}
                  />
                </PathGraphContext.Provider>
              )}
            </CollapsibleContentContainer>
            <CollapsibleContentContainer
              orderNumber={nextOrderNumber()}
              containerName="Specify a Frequency Cap for Comment Replies"
            >
              <FrequencyCapControl guard={promoter} chatbotId={chatbotId} />
            </CollapsibleContentContainer>
          </div>
          {promoter.pageId && (
            <div className="edit-comment-guard-panel__preview">
              <DialoguePreview
                dialogueId={promoter?.pageId}
                style={{ top: -20, marginTop: 20 }}
                sticky
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

const enhance = compose<InnerProps, OuterProps>(
  connect(mapFormStateToProps, null),
);

export default enhance(EditCommentGuardPanel);
